import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  VideoSettings as VideoSettingsIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  VideoLibrary as VideoLibraryIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';
import {jwtDecode} from 'jwt-decode';

function ChannelDetail() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    // Get current user ID from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log('🔍 JWT Token Debug:', {
          decoded: decoded,
          userId: decoded.userId,
          userID: decoded.userID,
          id: decoded.id
        });
        
        // Try different possible user ID field names
        const userId = decoded.userId || decoded.userID || decoded.id;
        setCurrentUserId(userId);
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }

    fetchChannelData();
  }, [channelId]);

  const fetchChannelData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch channel details and videos in parallel
      const [channelRes, videosRes] = await Promise.all([
        axios.get(API.channelById(channelId), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(API.channelVideos(channelId), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (channelRes.data.success) {
        setChannel(channelRes.data.channel);
      }

      if (videosRes.data.success) {
        setVideos(videosRes.data.videos || []);
      }
    } catch (err) {
      console.error('Error fetching channel data:', err);
      setError('Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  const isOwner = channel && currentUserId && channel.OwnerId === currentUserId;
  
  // Debug logging
  console.log('🔍 Owner Detection Debug:', {
    channel: channel,
    currentUserId: currentUserId,
    channelOwnerId: channel?.OwnerId,
    isOwner: isOwner,
    token: localStorage.getItem('authToken') ? 'present' : 'missing'
  });

  const handleToggleActive = async (videoId, isActive) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(API.toggleVideoActive(videoId), 
        { isActive }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data?.success) {
        setVideos(prevVideos => 
          prevVideos.map(video => 
            video.VideoID === videoId 
              ? { ...video, IsActive: isActive }
              : video
          )
        );
        setSuccessMsg(`Video ${isActive ? 'activated' : 'deactivated'} successfully`);
      } else {
        setError('Failed to update video status');
      }
    } catch (err) {
      console.error('Error toggling video status:', err);
      setError(err.response?.data?.error || 'Failed to update video status');
    }
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const res = await axios.delete(API.deleteVideo(videoToDelete.VideoID), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data?.success) {
        setVideos(prevVideos => prevVideos.filter(v => v.VideoID !== videoToDelete.VideoID));
        setSuccessMsg('Video deleted successfully');
        setDeleteDialogOpen(false);
        setVideoToDelete(null);
      } else {
        setError('Failed to delete video');
      }
    } catch (err) {
      console.error('Error deleting video:', err);
      setError(err.response?.data?.error || 'Failed to delete video');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!channel) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">Channel not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard/channels')}
        sx={{ mb: 2 }}
      >
        Back to Channels
      </Button>

      {/* Owner Banner */}
      {isOwner && (
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: 2,
            mb: 3,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                p: 1,
                borderRadius: 1,
              }}
            >
              <VideoLibraryIcon />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Your Channel
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage your content below
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<VideoSettingsIcon />}
              onClick={() => navigate(`/dashboard/channels/${channelId}/manage`)}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              Manage Videos
            </Button>
            <IconButton
              onClick={() => navigate(`/dashboard/channels/${channelId}/edit`)}
              sx={{ color: 'white' }}
            >
              <EditIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Channel Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h3" color="white">
              {channel.ChannelName?.[0]?.toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" gutterBottom>
              {channel.ChannelName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              by {channel.OwnerFirstName} {channel.OwnerLastName}
            </Typography>
            {channel.ChannelDescription && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                {channel.ChannelDescription}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Chip
                icon={<VideoLibraryIcon />}
                label={`${channel.ActiveVideoCount || 0} videos`}
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<VisibilityIcon />}
                label={`${channel.TotalViews || 0} views`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Videos Section */}
      <Typography variant="h5" gutterBottom>
        Videos ({videos.length})
      </Typography>

      {videos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <VideoLibraryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No videos in this channel yet
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Thumbnail</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Views</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {videos.map((video) => (
                <TableRow key={video.VideoID}>
                  <TableCell>
                    <Box
                      sx={{
                        width: 80,
                        height: 60,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'grey.200',
                      }}
                    >
                      {video.ThumbnailUrl ? (
                        <img
                          src={video.ThumbnailUrl}
                          alt={video.Title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: video.ThumbnailUrl ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        No Image
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        maxWidth: 200,
                      }}
                    >
                      {video.Title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<CategoryIcon />}
                      label={video.CategoryName}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VisibilityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {video.Views || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {video.UploadDate ? new Date(video.UploadDate).toLocaleDateString() : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={video.IsActive ? 'Active' : 'Inactive'}
                      color={video.IsActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <Switch
                        checked={!!video.IsActive}
                        color="primary"
                        onChange={(e) => handleToggleActive(video.VideoID, e.target.checked)}
                        size="small"
                      />
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/dashboard/videos/${video.VideoID}/edit`)}
                        title="Edit Video"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(video)}
                        title="Delete Video"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{videoToDelete?.Title}"?
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!error || !!successMsg}
        autoHideDuration={4000}
        onClose={() => {
          setError('');
          setSuccessMsg('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={error ? 'error' : 'success'}
          onClose={() => {
            setError('');
            setSuccessMsg('');
          }}
          sx={{ width: '100%' }}
        >
          {error || successMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ChannelDetail;

